import React, { useEffect, useRef } from 'react';
import { FiAlertTriangle } from 'react-icons/fi';
import { IoClose } from 'react-icons/io5';

interface ConfirmDeleteModalProps {
  isOpen: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  title?: string;
  message?: string;
}

/**
 * ConfirmDeleteModal - Standalone confirmation dialog for delete actions
 * 
 * Features:
 * - Warning design with red theme
 * - ESC key to cancel
 * - Focus trap and management
 * - Body scroll lock when open
 * - Customizable title and message
 */
export const ConfirmDeleteModal: React.FC<ConfirmDeleteModalProps> = ({
  isOpen,
  onConfirm,
  onCancel,
  title = 'Delete Section',
  message = 'Are you sure you want to delete this section? This action cannot be undone.',
}) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  const cancelButtonRef = useRef<HTMLButtonElement>(null);

  // Handle ESC key
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCancel();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onCancel]);

  // Lock body scroll and focus management
  useEffect(() => {
    if (!isOpen) return;

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // Focus the cancel button when modal opens
    setTimeout(() => {
      cancelButtonRef.current?.focus();
    }, 10);

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[99999] flex items-center justify-center p-4 bg-black/50 animate-fadeIn"
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-delete-title"
      aria-describedby="confirm-delete-message"
      onClick={onCancel}
    >
      <div
        ref={dialogRef}
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-scaleIn"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 bg-red-600 flex items-center justify-between">
          <h3 id="confirm-delete-title" className="text-lg font-semibold text-white">
            {title}
          </h3>
          <button
            type="button"
            onClick={onCancel}
            className="w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 
              flex items-center justify-center transition-colors"
            aria-label="Close dialog"
          >
            <IoClose className="text-white text-lg" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Warning Icon */}
          <div className="flex justify-center">
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center">
              <FiAlertTriangle className="w-8 h-8 text-red-600" />
            </div>
          </div>

          {/* Message */}
          <p
            id="confirm-delete-message"
            className="text-center text-gray-700 leading-relaxed"
          >
            {message}
          </p>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-2">
            <button
              ref={cancelButtonRef}
              type="button"
              onClick={onCancel}
              className="flex-1 px-4 py-2.5 border-2 border-gray-300 rounded-lg 
                hover:bg-gray-50 active:bg-gray-100 transition-colors 
                font-medium text-gray-700 focus:outline-none focus:ring-2 
                focus:ring-gray-400 focus:ring-offset-2"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onConfirm}
              className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-lg 
                hover:bg-red-700 active:bg-red-800 transition-colors 
                font-medium focus:outline-none focus:ring-2 
                focus:ring-red-500 focus:ring-offset-2"
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
